import { Payment, PaymentStatus } from '../types';
import { Address } from '../types/address';

export type BuyerPaymentDtoBackend = {
  id: string;
  orderId: string;
  orderNumber?: string | null;
  customerType?: string | null;
  status: PaymentStatus;
  amount: number;
  gatewayRef?: string | null;
  paidAt?: string | null;
  failedAt?: string | null;
  createdAt: string;
};

export const mapPaymentDto = (b: BuyerPaymentDtoBackend): Payment => ({
  id: b.id,
  orderId: b.orderId,
  amount: b.amount,
  status: b.status,
  // Backend doesn't carry payment method on the DTO; default to CARD.
  method: 'CARD',
  paidAt: b.paidAt ?? undefined,
  createdAt: b.createdAt,
});

type InvoiceLineDtoBackend = {
  productName?: string | null;
  qty: number;
  unitPrice: number;
  gstRate: number;
  lineTotal: number;
};

type InvoiceHeaderDtoBackend = {
  name?: string | null;
  abn?: string | null;
  address?: Address | null;
};

export type InvoiceDtoBackend = {
  header?: InvoiceHeaderDtoBackend | null;
  orderNumber?: string | null;
  customerType?: string | null;
  items?: InvoiceLineDtoBackend[] | null;
  subtotal: number;
  gstAmount: number;
  totalAmount: number;
  issuedAt?: string | null;
  paidAt?: string | null;
};

const formatAddress = (a?: Address | null): string => {
  if (!a) return '';
  return [a.line1, a.line2, a.suburb, a.state, a.postcode, a.country]
    .filter(Boolean)
    .join(', ');
};

/** Render a structured InvoiceDto as a readable plain-text tax invoice. */
export const renderInvoiceAsText = (inv: InvoiceDtoBackend): string => {
  const lines: string[] = [];
  lines.push('TAX INVOICE');
  lines.push('==================================');
  if (inv.header?.name) lines.push(`Bill to: ${inv.header.name}`);
  if (inv.header?.abn) lines.push(`ABN: ${inv.header.abn}`);
  if (inv.header?.address) lines.push(`Address: ${formatAddress(inv.header.address)}`);
  if (inv.orderNumber) lines.push(`Order: ${inv.orderNumber}`);
  if (inv.issuedAt) lines.push(`Issued: ${new Date(inv.issuedAt).toLocaleString()}`);
  if (inv.paidAt) lines.push(`Paid: ${new Date(inv.paidAt).toLocaleString()}`);
  lines.push('');
  lines.push('Items');
  lines.push('----------------------------------');
  (inv.items ?? []).forEach((line) => {
    lines.push(
      `${line.productName ?? ''} x${line.qty}  @ $${line.unitPrice.toFixed(2)}` +
        `  (GST ${(line.gstRate * 100).toFixed(0)}%)  = $${line.lineTotal.toFixed(2)}`,
    );
  });
  lines.push('----------------------------------');
  lines.push(`Subtotal:    $${inv.subtotal.toFixed(2)}`);
  lines.push(`GST:         $${inv.gstAmount.toFixed(2)}`);
  lines.push(`Total:       $${inv.totalAmount.toFixed(2)}`);
  return lines.join('\n');
};
